import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { seekSupabase } from '@/integrations/supabase/seekClient';
import { useAuth } from '@/contexts/AuthContext';
import type { ServiceKey } from '@/lib/serviceKeys';

export type EntitlementMode =
  | 'allowed'       // within plan limits
  | 'credit_topup'  // using credits beyond plan limit
  | 'hard_block'    // feature disabled globally
  | 'payment_required'
  | 'upgrade_required'  // over limit, no credits
  | 'insufficient_credits' // has some credits but not enough
  | 'trial';        // within active trial limits

export type LimitType = 'unlimited' | 'monthly' | 'active' | 'total';
export type EntitlementReason =
  | 'allowed'
  | 'backend_blocked'
  | 'cancelled'
  | 'candidate_lifecycle_blocked'
  | 'deactivated'
  | 'credit_overage_available'
  | 'expired'
  | 'feature_not_in_plan'
  | 'inactive'
  | 'insufficient_credits'
  | 'limit_exceeded'
  | 'loading'
  | 'no_subscription'
  | 'payment_required'
  | 'pending_payment'
  | 'plan_not_configured'
  | 'service_disabled'
  | 'suspended';

export interface EntitlementResult {
  allowed: boolean;
  reason: EntitlementReason;
  mode: EntitlementMode;
  used?: number;
  limit?: number;       // -1 = unlimited
  remaining?: number;   // -1 = unlimited
  creditBalance?: number;
  isLoading: boolean;
  isUnlimited: boolean;
  /** True when usage is at 80%+ of limit */
  isNearLimit: boolean;
  /** True when over plan limit but covered by credits */
  isUsingCredits: boolean;
  /** Percentage used (0-100, capped at 100) */
  usagePercent: number;
  /** True when entitlement is granted via an active trial */
  isTrial: boolean;
  /** Trial ID if active */
  trialId?: string;
  /** Limit type from plan_services config */
  limitType: LimitType;
}

interface EntitlementRpcResult {
  allowed?: boolean;
  reason?: string;
  mode?: string;
  used?: number;
  limit?: number;
  remaining?: number;
  credit_balance?: number;
  limit_type?: string;
  is_trial?: boolean;
  trial_id?: string;
}

function readEntitlementRpcResult(value: unknown): EntitlementRpcResult {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return {};
  const record = value as Record<string, unknown>;
  const numberValue = (key: string) => {
    const item = record[key];
    return typeof item === 'number' && Number.isFinite(item) ? item : undefined;
  };
  const stringValue = (key: string) => typeof record[key] === 'string' ? record[key] as string : undefined;

  return {
    allowed: typeof record.allowed === 'boolean' ? record.allowed : undefined,
    reason: stringValue('reason'),
    mode: stringValue('mode'),
    used: numberValue('used'),
    limit: numberValue('limit'),
    remaining: numberValue('remaining'),
    credit_balance: numberValue('credit_balance'),
    limit_type: stringValue('limit_type'),
    is_trial: typeof record.is_trial === 'boolean' ? record.is_trial : undefined,
    trial_id: stringValue('trial_id'),
  };
}

/** Maps limit_type to a human-readable period label */
export function getLimitTypeLabel(lt: LimitType): string {
  switch (lt) {
    case 'unlimited':
      return '';
    case 'active':
      return '(active)';
    case 'monthly':
      return '(this month)';
    case 'total':
      return '(lifetime)';
    default:
      return '';
  }
}

/**
 * Frontend entitlement hook for organization-owned services.
 * Calls check_entitlement RPC and returns structured result with usage data.
 */
export function useEntitlement(serviceKey: ServiceKey, unitsRequested = 1): EntitlementResult {
  const { profile } = useAuth();
  const orgId = profile?.org_id;

  const { data, isLoading } = useQuery({
    queryKey: ['entitlement', orgId, serviceKey, unitsRequested],
    queryFn: async (): Promise<EntitlementRpcResult> => {
      const { data: result, error } = await supabase.rpc('check_entitlement', {
        _owner_type: 'organization',
        _owner_id: orgId!,
        _service_key: serviceKey,
        _units_requested: unitsRequested,
      });
      if (error) {
        console.warn('[useEntitlement] RPC error, blocking until entitlement is verified:', error.message);
        return { allowed: false, reason: 'backend_blocked', mode: 'hard_block' };
      }
      return readEntitlementRpcResult(result);
    },
    enabled: !!orgId,
    staleTime: 60_000, // 1 minute — balance freshness with performance
    gcTime: 5 * 60_000,
  });

  const result = data || { allowed: false, reason: 'loading', mode: 'hard_block' };
  const used = result.used ?? 0;
  const limit = result.limit ?? -1;
  const remaining = result.remaining ?? -1;
  const creditBalance = result.credit_balance ?? 0;
  const isUnlimited = limit === -1 || result.limit_type === 'unlimited';
  const limitType: LimitType = (result.limit_type as LimitType) || (isUnlimited ? 'unlimited' : 'monthly');
  const usagePercent = isUnlimited || limit <= 0 ? 0 : Math.min(Math.round((used / limit) * 100), 100);

  return {
    allowed: result.allowed ?? false,
    reason: (result.reason ?? 'backend_blocked') as EntitlementReason,
    mode: (result.mode ?? 'hard_block') as EntitlementMode,
    used,
    limit,
    remaining,
    creditBalance,
    isLoading,
    isUnlimited,
    isNearLimit: !isUnlimited && limit > 0 && usagePercent >= 80,
    isUsingCredits: result.mode === 'credit_topup',
    usagePercent,
    isTrial: result.is_trial === true,
    trialId: result.trial_id,
    limitType,
  };
}

/**
 * Candidate entitlement hook for ForkSeek services.
 * Requires candidateId to be passed directly since SeekAuth context is separate.
 */
export function useCandidateEntitlement(serviceKey: string, candidateId: string | undefined, unitsRequested = 1): EntitlementResult {
  const { data, isLoading } = useQuery({
    queryKey: ['entitlement-candidate', candidateId, serviceKey, unitsRequested],
    queryFn: async (): Promise<EntitlementRpcResult> => {
      const { data: result, error } = await seekSupabase.rpc('check_entitlement', {
        _owner_type: 'candidate',
        _owner_id: candidateId!,
        _service_key: serviceKey,
        _units_requested: unitsRequested,
      });
      if (error) {
        console.warn('[useCandidateEntitlement] RPC error, blocking until entitlement is verified:', error.message);
        return { allowed: false, reason: 'backend_blocked', mode: 'hard_block' };
      }
      return readEntitlementRpcResult(result);
    },
    enabled: !!candidateId,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  const result = data || { allowed: false, reason: 'loading', mode: 'hard_block' };
  const used = result.used ?? 0;
  const creditBalance = result.credit_balance ?? 0;
  const limit = result.limit ?? -1;
  const remaining = result.remaining ?? -1;
  const isUnlimited = limit === -1 || result.limit_type === 'unlimited';
  const usagePercent = isUnlimited || limit <= 0 ? 0 : Math.min(Math.round((used / limit) * 100), 100);

  return {
    allowed: result.allowed ?? false,
    reason: (result.reason ?? 'backend_blocked') as EntitlementReason,
    mode: (result.mode ?? 'hard_block') as EntitlementMode,
    creditBalance,
    limit,
    isLoading,
    isUnlimited,
    isNearLimit: !isUnlimited && limit > 0 && usagePercent >= 80,
    isUsingCredits: result.mode === 'credit_topup',
    usagePercent,
    used,
    remaining,
    isTrial: result.is_trial === true,
    trialId: result.trial_id,
    limitType: (result.limit_type as LimitType) || (limit === -1 ? 'unlimited' : 'monthly'),
  };
}
